import { Snowflake, Sun, BookOpen, Users, Wifi, CalendarDays, Sparkles, ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { useCMS, usePage, useStat, useSection } from "@/hooks/useCMS";
import { Eyebrow, StaggeredHeading, PillButton, ArrowImage, Reveal, TiltCard } from "@/components/motifs";
import { CmsSection } from "@/components/CmsSection";
import { TemplateSections } from "@/components/TemplateSections";

const ICONS = { Snowflake, Sun, BookOpen, Users, Wifi, CalendarDays };
const TILTS = ["lg:-rotate-1", "lg:rotate-1", "lg:-rotate-2", "lg:rotate-2", "lg:-rotate-1", "lg:rotate-1"];
const MTS = ["lg:mt-0", "lg:mt-8", "lg:mt-4", "lg:mt-0", "lg:mt-8", "lg:mt-4"];

export default function Initiatives() {
  const { data } = useCMS();
  const page = usePage("initiatives");
  const initiatives = data.initiatives || [];
  const gallery = data.gallery || [];
  const winter = (data.campaigns || [])[0] || {};
  const secCards = useSection("in_cards");
  const statList = (data.stats || []).map((s) => ({ v: s.value, l: s.label }));
  const stats = statList.length ? statList : [
    { v: "500+", l: "communities" },
    { v: "50M+", l: "interactions" },
    { v: "12", l: "years of winter weeks" },
    { v: "10+", l: "years of NHF Day" },
  ];

  return (
    <div data-testid="initiatives-page" className="flex flex-col">
      {/* HERO */}
      <section className="mx-auto max-w-7xl px-4 sm:px-8 pt-14 pb-10 lg:pt-20">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <Eyebrow icon={Sparkles}>{page.eyebrow || "How we move Canada"}</Eyebrow>
            <div className="mt-5">
              <StaggeredHeading animate lines={[
                { text: page.headline_1 || "Big weeks." },
                { text: page.headline_2 || "Small moves.", emphasis: true, accent: "#2CACD4" },
                { text: page.headline_3 || "Every day." },
              ]} />
            </div>
            <p className="mt-6 max-w-md text-lg text-muted-foreground">{page.intro}</p>
          </div>
          <Reveal delay={0.1}>
            <ArrowImage src={gallery[4]?.url} alt={gallery[4]?.alt || "Wheelchair basketball"} className="h-[380px] rounded-r-3xl" />
          </Reveal>
        </div>
      </section>

      {/* WINTER BANNER */}
      <CmsSection sk="in_winter">
      <section className="mx-auto max-w-7xl px-4 sm:px-8 py-6">
        <div className="relative rotate-[-1deg]">
          <img src={winter.image} alt={winter.image_alt || winter.name || "Rink night"} className="h-72 w-full rounded-3xl object-cover shadow-xl" />
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-black/70 to-transparent" />
          <div className="absolute inset-y-0 left-0 flex items-center p-8 sm:p-12">
            <div className="max-w-md text-white">
              <p className="eyebrow" style={{ color: "#CAE295" }}>Rink nights are back</p>
              <h2 className="mt-3 font-display font-black uppercase text-2xl sm:text-4xl leading-tight">Winter Health & Fitness Week</h2>
              <p className="mt-2 text-white/80">Every February · 500+ communities lighting up the ice.</p>
              <div className="mt-5">
                <PillButton to="/Events#seasons" variant="dark" icon={ArrowRight} testid="winter-see-events">See events</PillButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      </CmsSection>
      {/* INITIATIVE CARDS */}
      <CmsSection sk="in_cards">
      <section className="mx-auto max-w-7xl px-4 sm:px-8 py-16 lg:py-24">
        <Reveal>
          <Eyebrow icon={CalendarDays}>{secCards.eyebrow || "Six flagship initiatives"}</Eyebrow>
          <h2 className="mt-3 font-display font-black uppercase text-3xl sm:text-4xl">{secCards.heading || "A hundred ways in."}</h2>
        </Reveal>
        <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {initiatives.map((it, i) => {
            const Icon = ICONS[it.icon] || Sparkles;
            return (
              <Reveal key={it.id || i} delay={(i % 3) * 0.1}>
                <TiltCard accent={it.accent} tilt={TILTS[i]} mt={MTS[i]} className="p-7 h-full flex flex-col" testid={`initiative-${i}`}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: `${it.accent}22`, color: it.accent }}>
                    <Icon size={22} />
                  </span>
                  <span className="mt-4 inline-block w-fit rounded-full px-3 py-1 text-xs font-bold text-white" style={{ background: it.accent }}>{it.tag}</span>
                  <h3 className="mt-3 font-display font-black uppercase text-xl">{it.name}</h3>
                  <p className="mt-2 text-sm text-muted-foreground flex-1">{it.blurb}</p>
                  <PillButton to={it.cta_link} variant="ghost" icon={ArrowUpRight} className="mt-4 w-fit px-0" testid={`initiative-cta-${i}`}>{it.cta_label}</PillButton>
                </TiltCard>
              </Reveal>
            );
          })}
        </div>
      </section>

      </CmsSection>
      {/* STATS STRIP */}
      <CmsSection sk="in_stats">
      <section className="bg-[#2CB6C2]/10 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 grid gap-8 sm:grid-cols-4 text-center">
          {stats.map((s) => (
            <Reveal key={s.l}>
              <p className="font-display font-black italic text-4xl sm:text-5xl text-[#DB1A5C]">{s.v}</p>
              <p className="mt-2 text-sm uppercase tracking-wider text-muted-foreground">{s.l}</p>
            </Reveal>
          ))}
        </div>
      </section>

      </CmsSection>
      {/* ONE CALENDAR */}
      <CmsSection sk="in_calendar">
      <section className="mx-auto max-w-7xl px-4 sm:px-8 py-16 lg:py-24 grid gap-12 lg:grid-cols-2 lg:items-center">
        <Reveal>
          <ArrowImage
            src={gallery[2]?.url}
            alt={gallery[2]?.alt || "Kids obstacle course"}
            className="h-[420px] rounded-l-3xl"
            overlay={
              <div className="absolute bottom-6 left-6 rounded-2xl bg-white/95 px-6 py-4 shadow-xl">
                <p className="font-display font-black italic text-2xl text-[#2CACD4]">Every Classroom</p>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">K–12</p>
              </div>
            }
          />
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display font-black uppercase text-3xl sm:text-4xl leading-tight">
            One calendar. A hundred ways in. <span className="italic text-[#DB1A5C]">Zero cost.</span>
          </h2>
          <ul className="mt-6 space-y-3">
            {["Always free — for participants and organizers", "Built with educators and clinicians", "Open to every age and every ability"].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#CAE295] text-slate-900"><Check size={14} strokeWidth={3} /></span>
                <span className="text-foreground/80">{t}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </section>

      </CmsSection>
      {/* FINAL CTA */}
      <CmsSection sk="in_cta">
      <section className="relative">
        <img src={gallery[5]?.url} alt="" className="h-[360px] w-full object-cover" aria-hidden="true" />
        <div className="absolute inset-0 bg-[#0F172A]/80" />
        <div className="absolute inset-0 flex items-center">
          <div className="mx-auto max-w-7xl px-4 sm:px-8 w-full text-white">
            <h2 className="font-display font-black uppercase text-3xl sm:text-5xl leading-[0.95] max-w-2xl">
              Pick an initiative. <span className="italic text-[#2CACD4]">Show us your moves.</span>
            </h2>
            <div className="mt-7 flex flex-wrap gap-4">
              <PillButton to="/Events" variant="dark" icon={ArrowRight} testid="init-find-event">Find an event</PillButton>
              <PillButton to="/BenniBooks" variant="pink" icon={BookOpen} testid="init-start-story">Start with a story</PillButton>
            </div>
          </div>
        </div>
      </section>
      </CmsSection>
      <TemplateSections page="initiatives" />
    </div>
  );
}
